import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider, type CredentialResponse } from "@react-oauth/google";
import { isTwoFactorChallenge, login, loginWithGoogle, register, verifyLogin2fa } from "../../lib/authApi";
import "../pulse/pulse.css";
import "./auth.css";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

type Mode = "login" | "register";

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once POST /auth/login comes back with a 2FA challenge instead of a
  // session - the form then renders the code-entry step in its place.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) return;
    setBusy(true);
    setError(null);
    try {
      await loginWithGoogle(response.credential);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Google sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    if (mode === "register" && password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        await register(email.trim(), password);
        navigate("/dashboard", { replace: true });
      } else {
        const result = await login(email.trim(), password);
        if (isTwoFactorChallenge(result)) {
          setChallengeToken(result.challenge_token);
          return;
        }
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setError("An account with this email already exists. Try signing in instead.");
      } else if (status === 401) {
        setError("Incorrect email or password.");
      } else {
        setError(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitTwoFactor = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !challengeToken) return;
    if (!twoFactorCode.trim()) {
      setError("Enter the 6-digit code from your authenticator app, or a recovery code.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyLogin2fa(challengeToken, twoFactorCode.trim());
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      setError(status === 401 ? "That code didn't work. Check your authenticator app and try again." : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pulse-onboarding-shell pulse-auth-shell">
      <div className="pulse-onboarding-chrome" />
      <main className="pulse-onboarding-center pulse-auth-center">
        <div className="pulse-onboarding-wordmark">
          <img src="/pulse-icon.png" alt="" aria-hidden="true" className="pulse-v2-brand-mark" />
          Pulse
        </div>
        <h1>{challengeToken ? "Enter your code" : mode === "login" ? "Welcome back" : "Create your account"}</h1>
        <p>
          {challengeToken
            ? "Enter the 6-digit code from your authenticator app, or one of your recovery codes."
            : mode === "login"
            ? "Sign in to pick up where you left off."
            : "Get started with your creator intelligence co-pilot."}
        </p>

        {challengeToken ? (
          <form className="pulse-auth-form" onSubmit={handleSubmitTwoFactor}>
            <label className="pulse-auth-field">
              <span>Authentication code</span>
              <input
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                value={twoFactorCode}
                onChange={(event) => setTwoFactorCode(event.target.value)}
                placeholder="123456 or a recovery code"
                autoFocus
                required
              />
            </label>

            {error ? <p className="pulse-onboarding-error">{error}</p> : null}

            <button type="submit" className="pulse-onboarding-primary" disabled={busy}>
              {busy ? "Verifying..." : "Verify"}
              {!busy ? <span aria-hidden="true">&rarr;</span> : null}
            </button>
            <p className="pulse-auth-switch">
              <button
                type="button"
                onClick={() => {
                  setChallengeToken(null);
                  setTwoFactorCode("");
                  setError(null);
                }}
              >
                Back to sign in
              </button>
            </p>
          </form>
        ) : (
          <>
            {GOOGLE_CLIENT_ID ? (
              <>
                <div className="pulse-auth-google">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError("Google sign-in failed. Please try again.")}
                    theme="filled_black"
                    shape="pill"
                    size="large"
                    width={360}
                    text="continue_with"
                  />
                </div>
                <div className="pulse-auth-divider"><span>or</span></div>
              </>
            ) : null}

            <form className="pulse-auth-form" onSubmit={handleSubmit}>
              <label className="pulse-auth-field">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </label>
              <label className="pulse-auth-field">
                <span>Password</span>
                <input
                  type="password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                  required
                />
              </label>

              {error ? <p className="pulse-onboarding-error">{error}</p> : null}

              <button type="submit" className="pulse-onboarding-primary" disabled={busy}>
                {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
                {!busy ? <span aria-hidden="true">&rarr;</span> : null}
              </button>
            </form>
          </>
        )}

        {!challengeToken ? (
          <p className="pulse-auth-switch">
            {mode === "login" ? (
              <>
                Don&apos;t have an account?{" "}
                <button type="button" onClick={() => { setMode("register"); setError(null); }}>
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button type="button" onClick={() => { setMode("login"); setError(null); }}>
                  Sign in
                </button>
              </>
            )}
          </p>
        ) : null}
      </main>
    </section>
  );
};

const LoginPage: React.FC = () => {
  if (!GOOGLE_CLIENT_ID) return <LoginForm />;
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginForm />
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
